/**
 * sobooth-product-options.js
 * Gestion interactive des options produit — sobooth.com
 *
 * Fonctionnalités :
 *  - Sélection boutons / swatches avec états actifs
 *  - Dropdown avec prix additionnels
 *  - Champ texte avec compteur de caractères
 *  - Checkbox avec prix additionnel
 *  - Upload de fichier avec validation taille + aperçu nom
 *  - Calcul total des options en temps réel
 *  - Validation avant ajout au panier
 *  - Transmission des choix via line_item_properties
 */

(function () {
  'use strict';

  const MAX_FILE_MB = 10;

  // ─── Initialisation ──────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('.sobooth-options').forEach(setupWidget);
  }

  function setupWidget(widget) {
    setupButtons(widget);
    setupDropdowns(widget);
    setupTextInputs(widget);
    setupCheckboxes(widget);
    setupUploads(widget);
    hookAddToCart(widget);
  }

  // ─── Boutons / Swatches ──────────────────────────────────────────────────────

  function setupButtons(widget) {
    widget.querySelectorAll('.sobooth-options__btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.dataset.group;
        // désélectionner les autres du même groupe
        widget.querySelectorAll(`.sobooth-options__btn[data-group="${group}"]`).forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        clearError(widget, group);
        updatePriceSummary(widget);
        syncPayload(widget);
      });
    });
  }

  // ─── Dropdowns ───────────────────────────────────────────────────────────────

  function setupDropdowns(widget) {
    widget.querySelectorAll('.sobooth-options__select').forEach(sel => {
      sel.addEventListener('change', () => {
        clearError(widget, sel.dataset.group);
        updatePriceSummary(widget);
        syncPayload(widget);
      });
    });
  }

  // ─── Texte libre ─────────────────────────────────────────────────────────────

  function setupTextInputs(widget) {
    widget.querySelectorAll('.sobooth-options__text').forEach(input => {
      const counter = input.closest('.sobooth-options__text-wrap')
        ?.querySelector('.sobooth-options__char-count');
      const max = parseInt(input.maxLength, 10) || 255;

      input.addEventListener('input', () => {
        const len = input.value.length;
        if (counter) counter.textContent = `${len} / ${max}`;
        if (len >= max * 0.9) counter?.classList.add('is-near-limit');
        else counter?.classList.remove('is-near-limit');
        clearError(widget, input.dataset.group);
        syncPayload(widget);
      });
    });
  }

  // ─── Checkboxes ──────────────────────────────────────────────────────────────

  function setupCheckboxes(widget) {
    widget.querySelectorAll('.sobooth-options__checkbox').forEach(cb => {
      cb.addEventListener('change', () => {
        updatePriceSummary(widget);
        syncPayload(widget);
      });
    });
  }

  // ─── Uploads ─────────────────────────────────────────────────────────────────

  function setupUploads(widget) {
    widget.querySelectorAll('.sobooth-options__upload-input').forEach(input => {
      const wrap = input.closest('.sobooth-options__upload-wrap');
      const filenameEl = wrap?.querySelector('.sobooth-options__upload-filename');
      const group = input.dataset.group;

      input.addEventListener('change', () => {
        const file = input.files[0];
        if (!file) return;

        if (file.size > MAX_FILE_MB * 1024 * 1024) {
          showError(widget, group, getUiString(widget, 'file_too_large'));
          input.value = '';
          if (filenameEl) filenameEl.textContent = getUiString(widget, 'no_file');
          return;
        }

        clearError(widget, group);
        if (filenameEl) filenameEl.textContent = file.name;
        // Note : les fichiers ne peuvent pas être envoyés via line_item_properties,
        // ils doivent être uploadés séparément (ex: Customer Hub ou Uploadery).
        // On note juste le nom du fichier dans le payload.
        syncPayload(widget);
      });
    });
  }

  // ─── Prix total des options ───────────────────────────────────────────────────

  function updatePriceSummary(widget) {
    let total = 0;
    const currency = widget.dataset.currency || '€';

    // Boutons actifs
    widget.querySelectorAll('.sobooth-options__btn.is-active').forEach(btn => {
      total += parseFloat(btn.dataset.price || 0);
    });

    // Dropdowns
    widget.querySelectorAll('.sobooth-options__select').forEach(sel => {
      const opt = sel.options[sel.selectedIndex];
      if (opt) total += parseFloat(opt.dataset.price || 0);
    });

    // Checkboxes
    widget.querySelectorAll('.sobooth-options__checkbox:checked').forEach(cb => {
      total += parseFloat(cb.dataset.price || 0);
    });

    const display = widget.querySelector('.sobooth-options__price-value');
    if (display) {
      display.dataset.optionsTotal = total;
      display.textContent = total > 0
        ? `+${formatMoney(total, currency)}`
        : '—';
    }

    // Dispatch event pour que le thème puisse mettre à jour le prix affiché
    widget.dispatchEvent(new CustomEvent('sobooth:options-price-updated', {
      bubbles: true,
      detail: {
        productId: widget.dataset.productId,
        optionsTotal: total,
        basePrice: parseInt(widget.dataset.basePrice || 0, 10)
      }
    }));
  }

  // ─── Payload → line_item_properties ─────────────────────────────────────────

  function syncPayload(widget) {
    const data = {};

    // Boutons
    widget.querySelectorAll('.sobooth-options__btn.is-active').forEach(btn => {
      data[btn.dataset.group] = btn.dataset.label;
    });

    // Dropdowns
    widget.querySelectorAll('.sobooth-options__select').forEach(sel => {
      const opt = sel.options[sel.selectedIndex];
      if (opt && opt.value) data[sel.dataset.group] = opt.dataset.label || opt.value;
    });

    // Texte
    widget.querySelectorAll('.sobooth-options__text').forEach(input => {
      if (input.value.trim()) data[input.dataset.group] = input.value.trim();
    });

    // Checkboxes
    widget.querySelectorAll('.sobooth-options__checkbox').forEach(cb => {
      const label = cb.closest('.sobooth-options__checkbox-label')
        ?.querySelector('.sobooth-options__checkbox-text')?.textContent?.trim();
      if (cb.checked) data[cb.dataset.group] = label || 'oui';
    });

    // Uploads (nom de fichier uniquement)
    widget.querySelectorAll('.sobooth-options__upload-input').forEach(input => {
      if (input.files[0]) data[input.dataset.group] = input.files[0].name;
    });

    const payload = widget.querySelector('.sobooth-options__payload');
    if (payload) payload.value = JSON.stringify(data);
  }

  // ─── Validation avant ATC ────────────────────────────────────────────────────

  function hookAddToCart(widget) {
    // Cherche le bouton ATC dans le même product-form
    const form = widget.closest('form[action="/cart/add"]')
      || document.querySelector('form[action="/cart/add"]');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      if (!validateWidget(widget)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        // Scroll vers la première erreur
        const firstError = widget.querySelector('.sobooth-options__error:not(:empty)');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Injecter les options comme line_item_properties individuelles
        injectProperties(widget, form);
      }
    }, true);
  }

  function validateWidget(widget) {
    let valid = true;

    // Groupes required buttons
    widget.querySelectorAll('.sobooth-options__group--buttons').forEach(group => {
      const groupId = group.dataset.groupId;
      const hasActive = group.querySelector('.sobooth-options__btn.is-active');
      const isRequired = !group.querySelector('.sobooth-options__btn[data-optional]');
      if (isRequired && !hasActive) {
        showError(widget, groupId, getUiString(widget, 'required_error'));
        valid = false;
      }
    });

    // Dropdowns required
    widget.querySelectorAll('.sobooth-options__group--dropdown').forEach(group => {
      const groupId = group.dataset.groupId;
      const sel = group.querySelector('.sobooth-options__select');
      if (sel && !sel.dataset.optional && !sel.value) {
        showError(widget, groupId, getUiString(widget, 'required_error'));
        valid = false;
      }
    });

    // Textes required
    widget.querySelectorAll('.sobooth-options__text[required]').forEach(input => {
      if (!input.value.trim()) {
        showError(widget, input.dataset.group, getUiString(widget, 'required_error'));
        valid = false;
      }
    });

    // Uploads required
    widget.querySelectorAll('.sobooth-options__upload-input[required]').forEach(input => {
      if (!input.files[0]) {
        showError(widget, input.dataset.group, getUiString(widget, 'required_error'));
        valid = false;
      }
    });

    return valid;
  }

  function injectProperties(widget, form) {
    const payload = JSON.parse(widget.querySelector('.sobooth-options__payload')?.value || '{}');
    Object.entries(payload).forEach(([key, value]) => {
      const existing = form.querySelector(`input[name="properties[${key}]"]`);
      if (existing) {
        existing.value = value;
      } else {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        form.appendChild(input);
      }
    });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  function showError(widget, groupId, msg) {
    const el = widget.querySelector(`.sobooth-options__error[data-for="${groupId}"]`);
    if (el) el.textContent = msg;
  }

  function clearError(widget, groupId) {
    const el = widget.querySelector(`.sobooth-options__error[data-for="${groupId}"]`);
    if (el) el.textContent = '';
  }

  function formatMoney(cents_or_euros, symbol) {
    // Shopify stocke les prix en centimes, mais les price_modifier sont en euros
    // → on affiche directement la valeur comme euros
    return `${parseFloat(cents_or_euros).toFixed(0)} ${symbol}`;
  }

  function getUiString(widget, key) {
    // Fallback strings côté JS (les vrais sont dans le snippet Liquid)
    const lang = widget.dataset.lang || 'fr';
    const strings = {
      required_error: { fr:'Ce champ est obligatoire', nl:'Verplicht veld', en:'Required field', de:'Pflichtfeld', es:'Campo obligatorio', it:'Campo obbligatorio', pt:'Campo obrigatório' },
      file_too_large: { fr:'Fichier trop volumineux (max. 10 Mo)', nl:'Bestand te groot (max. 10 MB)', en:'File too large (max. 10 MB)', de:'Datei zu groß (max. 10 MB)', es:'Archivo demasiado grande', it:'File troppo grande', pt:'Ficheiro demasiado grande' },
      no_file:        { fr:'Aucun fichier choisi', nl:'Geen bestand gekozen', en:'No file chosen', de:'Keine Datei', es:'Sin archivo', it:'Nessun file', pt:'Nenhum ficheiro' },
    };
    return strings[key]?.[lang] || strings[key]?.['fr'] || key;
  }

  // ─── Auto-init ────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support rechargement section Shopify (thème Dawn)
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('.sobooth-options').forEach(setupWidget);
  });

})();
